/*
 * Copyright (C) 2022-2025 - Jeci SARL - https://jeci.fr
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */

import AlfrescoProxyService from "./AlfrescoProxyService";

/**
 * Service for communicating with the Pristy Chat backend (Alfresco webscripts).
 * Handles workspace room creation and user token retrieval.
 */
class ChatBackendService extends AlfrescoProxyService {
  constructor() {
    super("ChatBackendService");
  }

  /**
   * Check if chat integration is enabled.
   *
   * @returns {Promise<{enabled: boolean}>}
   */
  async getStatus() {
    return this.get("fr/jeci/pristy/chat/status", {
      enabled: false,
    });
  }

  /**
   * Get the Rocket.Chat room information for a workspace.
   *
   * @param {string} siteId - The Alfresco site ID
   * @returns {Promise<{found: boolean, roomId?: string, roomName?: string}>}
   */
  async getWorkspaceRoom(siteId) {
    return this.get(`fr/jeci/pristy/chat/workspace/${siteId}/room`, {
      found: false,
    });
  }

  /**
   * Create a Rocket.Chat room for a workspace.
   *
   * @param {string} siteId - The Alfresco site ID
   * @returns {Promise<{roomId: string, roomName: string, created: boolean}|null>}
   */
  async createWorkspaceRoom(siteId) {
    return this.post(`fr/jeci/pristy/chat/workspace/${siteId}/room`);
  }

  /**
   * Get the current user's Rocket.Chat credentials.
   *
   * @returns {Promise<{hasCredentials: boolean, userId?: string, token?: string}>}
   */
  async getUserChatToken() {
    return this.get("fr/jeci/pristy/chat/user/token", {
      hasCredentials: false,
    });
  }

  /**
   * Save the current user's Rocket.Chat credentials.
   *
   * @param {string} userId - The Rocket.Chat user ID
   * @param {string} token - The Rocket.Chat personal access token
   * @returns {Promise<{success: boolean, message?: string}>}
   */
  async saveUserChatToken(userId, token) {
    return this.post(
      "fr/jeci/pristy/chat/user/token",
      { userId, token },
      { throwOnError: true },
    );
  }
}

export default new ChatBackendService();
